import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SearchAutocomplete.scss';

/**
 * SearchAutocomplete - Suggestion dropdown for SearchBar
 *
 * Presentational companion to `SearchBar`, rendered only when the parent opts in
 * with `enableAutocomplete`. It owns the suggestion fetch and the listbox; the
 * parent owns the query text, the form, and the input's ARIA wiring (which this
 * component feeds through `onOpenChange` / `onActiveOptionChange`).
 *
 * Behavior:
 * - Debounced GET to `endpoint?autocomplete=<query>` once the trimmed query reaches
 *   `minChars`; the endpoint answers with a plain JSON array of strings.
 * - ARIA 1.2 listbox popup: `role="listbox"` with `role="option"` children,
 *   ArrowDown/ArrowUp to move the active option, Enter to choose it, Escape to
 *   dismiss. Focus stays on the input; the active option travels as
 *   `aria-activedescendant`.
 * - Suggestions are a progressive enhancement. Any failure (network, non-2xx,
 *   malformed payload) clears the dropdown and leaves the plain form submit
 *   working.
 */

/**
 * Query-string parameter carrying the search text, matching Jellyfish
 * `SearchConstants.AUTOCOMPLETE`.
 */
const QUERY_PARAM = 'autocomplete';

/** Token in `announcementTemplate` replaced with the suggestion count. */
const COUNT_TOKEN = '{count}';
/** Announcement used when the caller supplies no template and there is one hit. */
const DEFAULT_ANNOUNCEMENT_ONE =
  '1 suggestion available, use the up and down arrow keys to review';
/** Announcement used when the caller supplies no template and there are several hits. */
const DEFAULT_ANNOUNCEMENT_MANY = `${COUNT_TOKEN} suggestions available, use the up and down arrow keys to review`;

export interface SearchAutocompleteProps {
  /** Current text in the search input. */
  query: string;
  /** Endpoint queried for suggestions. */
  endpoint: string;
  /** Minimum trimmed query length before querying. */
  minChars: number;
  /** Debounce in ms between the last keystroke and the query. */
  throttleMs: number;
  /** DOM id for the listbox, so the input can point at it with `aria-controls`. */
  listboxId: string;
  /** The search input, used to scope keyboard handling. */
  inputRef: React.RefObject<HTMLInputElement | null>;
  /** Called with the chosen suggestion (click or Enter on the active option). */
  onSelect: (suggestion: string) => void;
  /** Called when the listbox opens or closes. */
  onOpenChange?: (open: boolean) => void;
  /** Called with the active option's DOM id, or null when nothing is active. */
  onActiveOptionChange?: (optionId: string | null) => void;
  /** Accessible name for the listbox. */
  listboxLabel?: string;
  /**
   * Screen-reader announcement made when suggestions appear. Every `{count}`
   * token is replaced with the number of suggestions. Left unset, the English
   * default is used and it picks the singular or plural wording for the count.
   */
  announcementTemplate?: string;
  /**
   * Skips the suggestion fetch for the current text. The parent sets it for text
   * prefilled from the URL, which the user did not type, and clears it on the
   * first edit.
   */
  suppressQuery?: boolean;
}

/**
 * Coerces an autocomplete response into a clean suggestion list. Anything that
 * is not an array of non-blank strings yields an empty list rather than a throw,
 * so a bad payload degrades to "no suggestions".
 */
const normalizeSuggestions = (payload: unknown): string[] => {
  if (!Array.isArray(payload)) return [];
  const seen = new Set<string>();
  const suggestions: string[] = [];
  for (const entry of payload) {
    if (typeof entry !== 'string') continue;
    const trimmed = entry.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    suggestions.push(trimmed);
  }
  return suggestions;
};

export const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
  query,
  endpoint,
  minChars,
  throttleMs,
  listboxId,
  inputRef,
  onSelect,
  onOpenChange,
  onActiveOptionChange,
  listboxLabel = 'Search suggestions',
  announcementTemplate,
  suppressQuery = false,
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isDismissed, setIsDismissed] = useState(false);
  const rootRef = useRef<HTMLUListElement>(null);
  // Set by the arrow-key handlers so the scroll effect fires for keyboard moves
  // but not for hover.
  const shouldScrollActiveRef = useRef(false);
  // The suggestion the user just chose. The parent answers a selection by
  // setting the query to that text, so without this the debounce would fire a
  // request for the very string that was picked and reopen the dropdown
  // underneath the submit.
  const chosenQueryRef = useRef<string | null>(null);

  // Dismissal is an event, not a property of the text: it suppresses whatever
  // response belongs to the query that was on screen when the user dismissed,
  // and the fetch effect below lifts it on the next edit. Clearing the results
  // alongside the flag stops the previous query's suggestions flashing back
  // during the next debounce window.
  const isOpen = suggestions.length > 0 && !isDismissed;
  const optionId = useCallback(
    (index: number) => `${listboxId}-option-${index}`,
    [listboxId]
  );

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    setSuggestions([]);
    setActiveIndex(-1);
  }, []);

  const selectSuggestion = useCallback(
    (suggestion: string) => {
      chosenQueryRef.current = suggestion;
      dismiss();
      onSelect(suggestion);
    },
    [dismiss, onSelect]
  );

  // Debounced suggestion fetch. The cleanup cancels both the pending debounce
  // and any in-flight request, so only the newest query can land.
  useEffect(() => {
    if (suppressQuery) {
      return undefined;
    }

    // This effect already re-runs on every query change, so it is where an
    // earlier dismissal is lifted: comparing the query against the dismissed
    // string instead would shut the listbox for that exact text for the rest of
    // the component's life, so backspacing 'sell' to 'sel' and retyping the 'l'
    // could never reopen it. A dismissal made while a request is in flight still
    // stands, because nothing here re-runs until the query itself changes.
    setIsDismissed(false);

    const trimmed = query.trim();
    if (chosenQueryRef.current !== null && chosenQueryRef.current !== query) {
      // The query moved off the chosen suggestion, so it is ordinary text again.
      chosenQueryRef.current = null;
    }
    if (chosenQueryRef.current === query) {
      // Nothing to suggest for a string the user just picked from this list.
      return undefined;
    }
    if (trimmed.length < minChars) {
      setSuggestions([]);
      setActiveIndex(-1);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const response = await fetch(
            `${endpoint}?${QUERY_PARAM}=${encodeURIComponent(trimmed)}`,
            { signal: controller.signal }
          );
          if (!response.ok) {
            throw new Error(`Autocomplete failed: HTTP ${response.status}`);
          }
          const payload: unknown = await response.json();
          if (controller.signal.aborted) return;
          setSuggestions(normalizeSuggestions(payload));
          setActiveIndex(-1);
        } catch {
          // Handled, not swallowed: suggestions are optional, so a failed or
          // aborted request collapses the dropdown and the form still submits.
          // The aborted case is skipped so a superseded request cannot clear
          // results that a newer one already delivered.
          if (controller.signal.aborted) return;
          setSuggestions([]);
          setActiveIndex(-1);
        }
      })();
    }, throttleMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, endpoint, minChars, throttleMs, suppressQuery]);

  const isInside = useCallback(
    (node: Node | null) =>
      node !== null && (node === inputRef.current || rootRef.current?.contains(node) === true),
    [inputRef]
  );

  // Close when interaction leaves the input and the listbox, either a pointer
  // press elsewhere on the page or Tab moving focus away. Without this the
  // listbox floats over the page with no way out: the keydown handler below is
  // scoped to events originating inside the component, so once focus has left,
  // Escape can no longer reach it either.
  useEffect(() => {
    if (!isOpen) return undefined;

    const dismissIfOutside = (event: Event) => {
      if (!isInside(event.target as Node | null)) {
        dismiss();
      }
    };
    // focusout reports where focus is GOING via relatedTarget, and unlike focusin
    // it still fires when focus leaves the document entirely (Tab out of the last
    // field), where relatedTarget is null.
    const dismissIfFocusLeft = (event: FocusEvent) => {
      if (!isInside(event.relatedTarget as Node | null)) {
        dismiss();
      }
    };

    document.addEventListener('pointerdown', dismissIfOutside);
    document.addEventListener('focusout', dismissIfFocusLeft);
    return () => {
      document.removeEventListener('pointerdown', dismissIfOutside);
      document.removeEventListener('focusout', dismissIfFocusLeft);
    };
  }, [isOpen, dismiss, isInside]);

  // Document-level keydown, matching the MegaMenu pattern, scoped to events
  // originating in the input or the dropdown itself.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      // An IME candidate list navigates with the same keys. Safari and Firefox
      // report the real key with isComposing set while a composition is active,
      // so without this guard ArrowDown/ArrowUp/Enter aimed at the candidate
      // list would move this listbox's active option instead. Chrome and Edge
      // report key: 'Process' during composition, which no case below matches.
      if (event.isComposing) return;
      if (!isInside(event.target as Node | null)) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          shouldScrollActiveRef.current = true;
          setActiveIndex((current) => (current + 1) % suggestions.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          shouldScrollActiveRef.current = true;
          setActiveIndex((current) =>
            current <= 0 ? suggestions.length - 1 : current - 1
          );
          break;
        case 'Enter':
          // With no active option the keystroke falls through to the form, which
          // submits the typed query as it always has.
          if (activeIndex >= 0) {
            event.preventDefault();
            selectSuggestion(suggestions[activeIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          dismiss();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, suggestions, activeIndex, dismiss, selectSuggestion, isInside]);

  // Report ARIA state upward; the input carries aria-expanded / aria-controls /
  // aria-activedescendant because it owns the combobox role.
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    onActiveOptionChange?.(activeIndex >= 0 ? optionId(activeIndex) : null);
  }, [activeIndex, optionId, onActiveOptionChange]);

  // The list scrolls, and focus never leaves the input, so the browser will not
  // follow the active option for us. Only keyboard moves scroll: doing it for
  // hover would drag the list out from under the pointer. Options are direct
  // children in order, so the index addresses them without a selector.
  useEffect(() => {
    if (activeIndex < 0 || !shouldScrollActiveRef.current) return;
    shouldScrollActiveRef.current = false;
    const activeOption = rootRef.current?.children[activeIndex];
    (activeOption as HTMLElement | undefined)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  // The listbox appearing is silent to a screen reader: focus never moves into
  // it, so only aria-activedescendant speaks, and only once the user starts
  // arrowing. This announces availability. The region is mounted even while
  // closed, because a live region added at the same moment as its content is
  // announced unreliably. The wording is a caller-supplied, translatable
  // template so a localized document does not announce English.
  const announcement = isOpen
    ? (
        announcementTemplate ??
        (suggestions.length === 1 ? DEFAULT_ANNOUNCEMENT_ONE : DEFAULT_ANNOUNCEMENT_MANY)
      )
        .split(COUNT_TOKEN)
        .join(String(suggestions.length))
    : '';

  return (
    <>
      <div role="status" aria-live="polite" aria-atomic="true" className="poly-visually-hidden">
        {announcement}
      </div>
      {isOpen && (
        <ul
          ref={rootRef}
          id={listboxId}
          role="listbox"
          aria-label={listboxLabel}
          className="poly-search-autocomplete"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              className={`poly-search-autocomplete__option${
                index === activeIndex ? ' poly-search-autocomplete__option--active' : ''
              }`}
              // mousedown default is suppressed so the click does not pull focus
              // off the input before the selection is handled.
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectSuggestion(suggestion)}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </>
  );
};
